import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CommandType, isRepoNotRequired, UsedCommandType } from '~/constants/commands'
import { getTaskRequest, TaskContext } from '~/contexts/TaskContext'
import { useFinishedIndexes } from './organization'

export interface RepositoryBranch {
  conn_type: string
  repository: string
  branch: string
}

export function useCodeContext() {
  // Safe task context access (doesn't throw if outside TaskProvider)
  const taskContext = useContext(TaskContext)
  const task = taskContext?.currentTask?.task
  const [searchParams] = useSearchParams()

  // Track if URL param was used for initialization (prevents task sync from overwriting)
  const initializedFromUrlRef = useRef(false)

  // Helper to get context from task (Priority 1)
  const getContextFromTask: () => {
    repositoryBranches: RepositoryBranch[]
    s3UploadPaths: string[]
  } = useCallback(() => {
    if (!getTaskRequest(task)?.metadata) {
      return {
        repositoryBranches: [],
        s3UploadPaths: []
      }
    }

    const metadata = getTaskRequest(task)?.metadata

    if (metadata?.codeContext) {
      const codeContext = metadata.codeContext
      return {
        repositoryBranches: [
          {
            conn_type: codeContext.connType,
            repository:
              codeContext.orgName && codeContext.repoName
                ? `${codeContext.orgName}/${codeContext.repoName}`
                : codeContext.repoName,
            branch: codeContext.branchName
          }
        ],
        s3UploadPaths: [codeContext.s3TarFilePath]
      }
    }

    if (metadata?.multiRepoCodeContext && metadata.multiRepoCodeContext.length > 0) {
      return {
        repositoryBranches: metadata.multiRepoCodeContext.map(ctx => ({
          conn_type: ctx.connType,
          repository: ctx.orgName && ctx.repoName ? `${ctx.orgName}/${ctx.repoName}` : ctx.repoName,
          branch: ctx.branchName
        })),
        s3UploadPaths: metadata.multiRepoCodeContext.map(ctx => ctx.s3TarFilePath)
      }
    }
    return {
      repositoryBranches: [],
      s3UploadPaths: []
    }
  }, [task])

  const [repositories, setRepositories] = useState<RepositoryBranch[]>([])
  const [s3UploadPaths, setS3UploadPaths] = useState<string[]>([])

  // Priority 1: Initialize from URL param (for plugin uploads)
  // This runs first and sets the flag to prevent task sync from overwriting
  // Note: URL param is NOT cleared here - it's preserved for navigation between commands
  useEffect(() => {
    const ideS3TarFilePath = searchParams.get('s3_tar_file_path')
    if (ideS3TarFilePath && !initializedFromUrlRef.current) {
      setS3UploadPaths([ideS3TarFilePath])
      initializedFromUrlRef.current = true
    }
  }, [searchParams])

  // Priority 2: Sync from task metadata (for editing existing tasks)
  // IMPORTANT: Skip if URL param was used - prevents overwriting plugin uploads
  useEffect(() => {
    // Skip if initialized from URL param
    if (initializedFromUrlRef.current) return

    // Only sync from task if task has metadata
    if (!task) return

    const data = getContextFromTask()
    setRepositories(data.repositoryBranches)
    setS3UploadPaths(data.s3UploadPaths)
  }, [task, getContextFromTask])

  return {
    repositories,
    setRepositories,
    s3UploadPaths,
    setS3UploadPaths
  }
}

export function useRepositoryBranches(command: UsedCommandType) {
  const { repos, branches } = useFinishedIndexes({
    exculdeInternalRepos: command === CommandType.CODE_REVIEW,
    commandType: command
  })

  const getCommonRepositoriesByBranch = useCallback(
    (
      branches: Record<string, Record<string, string[]>>,
      branchName?: string,
      connType?: string
    ): string[] => {
      if (!branchName || !branches) {
        return []
      }

      const repositories: string[] = []

      // If connType is specified, only search within that connection type
      if (connType && branches[connType]) {
        const connBranches = branches[connType]

        Object.entries(connBranches).forEach(([repo, repoBranches]) => {
          if (Array.isArray(repoBranches) && repoBranches.includes(branchName)) {
            repositories.push(repo)
          }
        })
      }
      // Otherwise search across all connection types
      else {
        Object.values(branches).forEach(connBranches => {
          Object.entries(connBranches).forEach(([repo, repoBranches]) => {
            if (Array.isArray(repoBranches) && repoBranches.includes(branchName)) {
              repositories.push(repo)
            }
          })
        })
      }

      return repositories
    },
    []
  )

  const getBranches = useCallback(
    (conn_type?: string, repository?: string) => {
      if (conn_type && repository) {
        const repositoryBranches = branches?.[conn_type]?.[repository]
        return Array.isArray(repositoryBranches) ? repositoryBranches : []
      }
      if (isRepoNotRequired(command)) {
        const allBranches = Object.values(branches)
          .flatMap(repo => Object.values(repo))
          .flat()
        return [...new Set(allBranches)]
      }
      return []
    },
    [branches, command]
  )

  return { repos, branches, getCommonRepositoriesByBranch, getBranches }
}
